import {
	Component,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChanges,
} from '@angular/core';
import { ToastService } from '@core/components/toasts/toasts.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { EmployeeFeedbackService } from 'services/feedback/employeeFeedback.service';
import { EmployeeFeedbackModel } from 'services/models/employeeFeedback.model';
import { QueryParams } from 'services/models/odata/queryParams';
import { DateSelectOutput } from 'app/views/shared/components/date-ranger-picker/date-ranger-picker.component';

export type FeedbackTypeKey = 'keepDoing' | 'stopDoing' | 'startDoing';

export interface FeedbackTypedItem {
	feedbackId: string;
	type: FeedbackTypeKey;
	typeLabel: string;
	description: string;
	creationDate: string;
	evaluatorName: string;
	evaluatorJobTitle: string;
	evaluatorInitials: string;
}

interface FeedbackColumnConfig {
	type: FeedbackTypeKey;
	title: string;
	cssClass: string;
}

const TYPE_META: Record<FeedbackTypeKey, { label: string; order: number }> = {
	keepDoing: { label: 'Continuar fazendo', order: 0 },
	stopDoing: { label: 'Parar de fazer', order: 1 },
	startDoing: { label: 'Começar a fazer', order: 2 },
};

@Component({
	selector: 'app-employee-details-feedbacks',
	templateUrl: './employee-details-feedbacks.component.html',
	styleUrls: ['./employee-details-feedbacks.component.scss'],
})
export class EmployeeDetailsFeedbacksComponent implements OnChanges, OnDestroy {
	@Input() employeeId: string | null | undefined;

	readonly columns: FeedbackColumnConfig[] = [
		{ type: 'keepDoing', title: 'Continuar fazendo', cssClass: 'keep' },
		{ type: 'stopDoing', title: 'Parar de fazer', cssClass: 'stop' },
		{ type: 'startDoing', title: 'Começar a fazer', cssClass: 'start' },
	];

	isLoading = false;
	hasLoadError = false;
	allItems: FeedbackTypedItem[] = [];
	filteredItems: FeedbackTypedItem[] = [];
	pagedItems: FeedbackTypedItem[] = [];
	itemsByType: Record<FeedbackTypeKey, FeedbackTypedItem[]> = {
		keepDoing: [],
		stopDoing: [],
		startDoing: [],
	};
	count = 0;

	queryParams: QueryParams = Object.assign(new QueryParams(), {
		top: 8,
		skip: 0,
		page: 1,
		orderBy: 'creationDate',
		orderByOrder: 'desc' as 'asc' | 'desc',
	});

	currentSort: { prop: string; dir: 'asc' | 'desc' } = {
		prop: 'creationDate',
		dir: 'desc',
	};

	columnFilterVisibility = {
		creationDate: false,
		evaluatorName: false,
	};

	evaluatorColumnQuery = '';
	selectedDate: string = null;
	selectedStartDate: string = null;
	selectedEndDate: string = null;

	todayDate: NgbDateStruct = {
		year: moment().year(),
		month: moment().month() + 1,
		day: moment().date(),
	};

	private readonly destroy$ = new Subject<void>();
	private readonly filterSubject = new Subject<void>();
	private loadSubscription?: Subscription;
	private loadedEmployeeId: string | null = null;

	constructor(
		private readonly feedbackService: EmployeeFeedbackService,
		private readonly toastService: ToastService,
	) {
		this.filterSubject
			.pipe(debounceTime(300), takeUntil(this.destroy$))
			.subscribe(() => {
				this.queryParams.skip = 0;
				this.queryParams.page = 1;
				this.applyFiltersAndPaging();
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.employeeId) {
			return;
		}

		const id = changes.employeeId.currentValue;
		if (!id) {
			this.resetState();
			return;
		}

		if (id !== this.loadedEmployeeId) {
			this.loadFeedbacks(id);
		}
	}

	ngOnDestroy(): void {
		this.loadSubscription?.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		const target = event.target as HTMLElement | null;
		if (target?.closest('.date-range-picker-popup')) {
			return;
		}
		if (target?.closest('.feedback-column-filter-header')) {
			return;
		}
		this.closeAllColumnFilters();
	}

	getItemsByType(type: FeedbackTypeKey): FeedbackTypedItem[] {
		return this.itemsByType[type] || [];
	}

	getCountByType(type: FeedbackTypeKey): number {
		return this.itemsByType[type]?.length || 0;
	}

	onSort(event: { sorts?: Array<{ prop: string; dir: string }> }): void {
		if (!event?.sorts?.length) {
			return;
		}

		const [sort] = event.sorts;
		const prop = sort.prop;
		if (prop === 'description') {
			return;
		}

		const dir = sort.dir === 'asc' ? 'asc' : 'desc';
		this.currentSort = { prop, dir };
		this.queryParams.orderBy = prop;
		this.queryParams.orderByOrder = dir;
		this.applyFiltersAndPaging();
	}

	onPage(event: { offset: number; limit: number }): void {
		this.queryParams.skip = event.offset * event.limit;
		this.queryParams.top = event.limit;
		this.queryParams.page = event.offset + 1;
		this.applyFiltersAndPaging();
	}

	onPaginatorQuery(): void {
		const top = this.queryParams.top || 8;
		const page = this.queryParams.page || 1;
		this.queryParams.skip = top * (page - 1);
		this.applyFiltersAndPaging();
	}

	toggleColumnFilter(column: 'creationDate' | 'evaluatorName'): void {
		const isOpen = this.columnFilterVisibility[column];
		this.columnFilterVisibility = {
			creationDate: false,
			evaluatorName: false,
			[column]: !isOpen,
		};
	}

	onEvaluatorFilterChange(): void {
		this.filterSubject.next();
	}

	onDateRangeChange(selection: DateSelectOutput): void {
		this.setSelectedDateRange(selection?.fromDate || null, selection?.toDate || null);
		this.filterSubject.next();
	}

	clearColumnFilter(column: 'creationDate' | 'evaluatorName'): void {
		if (column === 'evaluatorName') {
			this.evaluatorColumnQuery = '';
		}

		if (column === 'creationDate') {
			this.setSelectedDateRange(null, null);
		}

		this.columnFilterVisibility[column] = false;
		this.filterSubject.next();
	}

	private loadFeedbacks(employeeId: string): void {
		this.isLoading = true;
		this.hasLoadError = false;
		this.loadedEmployeeId = employeeId;
		this.loadSubscription?.unsubscribe();

		const params = Object.assign(new QueryParams(), {
			top: 500,
			skip: 0,
			orderBy: 'creationDate',
			orderByOrder: 'desc' as 'asc' | 'desc',
			select: [
				'id',
				'creationDate',
				'startDoing',
				'keepDoing',
				'stopDoing',
				'employeeManagerName',
				'employeeManagerJobTitle',
			],
		});

		this.loadSubscription = this.feedbackService.listByEmployee(employeeId, params).subscribe({
			next: (response) => {
				const feedbacks = (response?.data || []) as EmployeeFeedbackModel[];
				this.allItems = this.flattenFeedbacks(feedbacks);
				this.rebuildItemsByType();
				this.queryParams.skip = 0;
				this.queryParams.page = 1;
				this.applyFiltersAndPaging();
				this.hasLoadError = false;
				this.isLoading = false;

				const totalFromApi = Number(response?.count || feedbacks.length);
				if (totalFromApi > 500) {
					this.toastService.showWarning(
						'Exibindo os 500 feedbacks mais recentes deste colaborador.'
					);
				}
			},
			error: () => {
				this.resetState();
				this.hasLoadError = true;
				this.isLoading = false;
				this.toastService.showError('Não foi possível carregar os feedbacks do colaborador.');
			},
		});
	}

	private flattenFeedbacks(feedbacks: EmployeeFeedbackModel[]): FeedbackTypedItem[] {
		const items: FeedbackTypedItem[] = [];

		for (const feedback of feedbacks || []) {
			const base = {
				feedbackId: feedback.id,
				creationDate: feedback.creationDate,
				evaluatorName: feedback.employeeManagerName || '',
				evaluatorJobTitle: feedback.employeeManagerJobTitle || '',
				evaluatorInitials: this.getInitials(feedback.employeeManagerName),
			};

			const typedFields: Array<{ type: FeedbackTypeKey; value: string }> = [
				{ type: 'keepDoing', value: feedback.keepDoing },
				{ type: 'stopDoing', value: feedback.stopDoing },
				{ type: 'startDoing', value: feedback.startDoing },
			];

			for (const field of typedFields) {
				const description = (field.value || '').trim();
				if (!description) {
					continue;
				}

				items.push({
					...base,
					type: field.type,
					typeLabel: TYPE_META[field.type].label,
					description,
				});
			}
		}

		return items.sort((a, b) => this.compareDatesDesc(a.creationDate, b.creationDate));
	}

	private rebuildItemsByType(): void {
		this.itemsByType = {
			keepDoing: this.allItems
				.filter((item) => item.type === 'keepDoing')
				.sort((a, b) => this.compareDatesDesc(a.creationDate, b.creationDate)),
			stopDoing: this.allItems
				.filter((item) => item.type === 'stopDoing')
				.sort((a, b) => this.compareDatesDesc(a.creationDate, b.creationDate)),
			startDoing: this.allItems
				.filter((item) => item.type === 'startDoing')
				.sort((a, b) => this.compareDatesDesc(a.creationDate, b.creationDate)),
		};
	}

	private applyFiltersAndPaging(): void {
		let items = [...this.allItems];

		const evaluatorQuery = (this.evaluatorColumnQuery || '').trim().toLowerCase();
		if (evaluatorQuery) {
			items = items.filter((item) =>
				(item.evaluatorName || '').toLowerCase().includes(evaluatorQuery)
			);
		}

		const dateRange = this.getDateRange();
		if (dateRange) {
			const start = moment(dateRange.startDate).startOf('day');
			const end = moment(dateRange.endDate).endOf('day');
			items = items.filter((item) => {
				const date = moment(item.creationDate);
				return date.isValid() && date.isSameOrAfter(start) && date.isSameOrBefore(end);
			});
		}

		items = this.sortItems(items);
		this.filteredItems = items;
		this.count = items.length;

		const top = this.queryParams.top || 8;
		const skip = this.queryParams.skip || 0;
		this.pagedItems = items.slice(skip, skip + top);
	}

	private sortItems(items: FeedbackTypedItem[]): FeedbackTypedItem[] {
		const prop = this.currentSort?.prop || 'creationDate';
		const dir = this.currentSort?.dir === 'asc' ? 1 : -1;

		return [...items].sort((a, b) => {
			if (prop === 'creationDate') {
				const diff = moment(a.creationDate).valueOf() - moment(b.creationDate).valueOf();
				return diff * dir;
			}

			if (prop === 'type' || prop === 'typeLabel') {
				const orderA = TYPE_META[a.type].order;
				const orderB = TYPE_META[b.type].order;
				return (orderA - orderB) * dir;
			}

			if (prop === 'evaluatorName') {
				return (a.evaluatorName || '').localeCompare(b.evaluatorName || '', 'pt-BR') * dir;
			}

			return 0;
		});
	}

	private setSelectedDateRange(startDate: string | null, endDate: string | null): void {
		this.selectedStartDate = startDate;
		this.selectedEndDate = endDate;

		if (!startDate) {
			this.selectedDate = null;
			return;
		}

		this.selectedDate = endDate ? `${startDate}-${endDate}` : startDate;
	}

	private getDateRange(): { startDate: string; endDate: string } | null {
		const startDate = this.selectedStartDate || this.selectedDate;
		if (!startDate) {
			return null;
		}

		return {
			startDate,
			endDate: this.selectedEndDate || startDate,
		};
	}

	private closeAllColumnFilters(): void {
		this.columnFilterVisibility = {
			creationDate: false,
			evaluatorName: false,
		};
	}

	private getInitials(name: string): string {
		const parts = (name || '')
			.trim()
			.split(/\s+/)
			.filter(Boolean);

		if (!parts.length) {
			return '?';
		}

		if (parts.length === 1) {
			return parts[0].substring(0, 2).toUpperCase();
		}

		return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
	}

	private compareDatesDesc(a: string, b: string): number {
		return moment(b).valueOf() - moment(a).valueOf();
	}

	private resetState(): void {
		this.allItems = [];
		this.filteredItems = [];
		this.pagedItems = [];
		this.itemsByType = {
			keepDoing: [],
			stopDoing: [],
			startDoing: [],
		};
		this.count = 0;
		this.loadedEmployeeId = null;
		this.hasLoadError = false;
	}
}
